import React, { useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Command } from "cmdk";
import { getRecents } from "../navigation/navLauncherStorage";
import "./CommandPalette.css";

/**
 * Global command menu (Cmd/Ctrl+K). Entries must already be permission-filtered.
 */
const CommandPalette = ({ open, onOpenChange, entries, loading }) => {
  const navigate = useNavigate();

  const recents = useMemo(() => {
    if (!open) return [];
    return getRecents();
  }, [open]);

  useEffect(() => {
    const onKeyDown = (e) => {
      if ((e.key === "k" || e.key === "K") && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        onOpenChange(!open);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, onOpenChange]);

  const grouped = useMemo(() => {
    const g = {};
    (entries || []).forEach((en) => {
      const h = en.group || "Pages";
      if (!g[h]) g[h] = [];
      g[h].push(en);
    });
    return g;
  }, [entries]);

  const handleSelect = useCallback(
    (path) => {
      if (path) {
        navigate(path);
        onOpenChange(false);
      }
    },
    [navigate, onOpenChange],
  );

  return (
    <Command.Dialog
      open={open}
      onOpenChange={onOpenChange}
      label="Command palette"
      overlayClassName="command-palette-overlay"
      contentClassName="command-palette-content"
    >
      <div className="command-palette-search-wrap">
        <Command.Input
          placeholder="Search pages and actions…"
          className="command-palette-input"
          autoFocus
        />
      </div>
      <Command.List className="command-palette-list">
        {loading ? (
          <div className="command-palette-empty">Loading navigation…</div>
        ) : (
          <>
            <Command.Empty className="command-palette-empty">
              No results found.
            </Command.Empty>

            {recents.length > 0 && (
              <Command.Group heading="Suggestions" className="command-palette-group">
                {recents.map((r) => (
                  <Command.Item
                    key={`recent-${r.path}`}
                    value={`${r.label} suggestions recent ${r.path}`.toLowerCase()}
                    onSelect={() => handleSelect(r.path)}
                    className="command-palette-item"
                  >
                    <div className="command-palette-item-main">
                      <span className="command-palette-item-label">{r.label}</span>
                      <span className="command-palette-item-group">Recently viewed</span>
                    </div>
                  </Command.Item>
                ))}
              </Command.Group>
            )}

            {Object.entries(grouped).map(([heading, items]) => (
              <Command.Group
                key={heading}
                heading={heading}
                className="command-palette-group"
              >
                {items.map((item) => (
                  <Command.Item
                    key={item.id}
                    value={`${item.label} ${(item.keywords || []).join(" ")} ${item.path}`.toLowerCase()}
                    onSelect={() => handleSelect(item.path)}
                    className="command-palette-item"
                  >
                    <div className="command-palette-item-main">
                      <span className="command-palette-item-label">{item.label}</span>
                      <span className="command-palette-item-group">{heading}</span>
                    </div>
                    <span className="command-palette-item-path">{item.path}</span>
                  </Command.Item>
                ))}
              </Command.Group>
            ))}
          </>
        )}
      </Command.List>
      <div className="command-palette-footer" aria-hidden="true">
        <span>↑↓ Navigate</span>
        <span>↵ Open</span>
        <span>Esc Close</span>
      </div>
    </Command.Dialog>
  );
};

export default CommandPalette;
